import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
  Stack,
} from "@mui/material";
import React, { useEffect } from "react";
import { TablePaginationActions } from "../pagination";
import { ConfirmV2ResultType } from "../types";

interface ResultProps {
  totalData: number;
  result: ConfirmV2ResultType;
  featureType: string;
}

const generateTableForProduct = (
  result: any[],
  totalData: number,
  rowsPerPage: number,
  page: number,
  handleChangePage: (
    event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number,
  ) => void,
  handleChangeRowsPerPage: (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => void,
) => {
  const rowElement: JSX.Element[] = [];
  result.forEach((row: any) => {
    rowElement.push(
      <TableRow key={`${row.key.company_id}-${row.key.product_ref}`}>
        <TableCell>{row.key.price_group}</TableCell>
        <TableCell>{row.key.company_id}</TableCell>
        <TableCell>{row.key.product_ref}</TableCell>
        <TableCell>
          {row.company_id ? "Failed To Assign" : "Failed To Import Product"}
        </TableCell>
        <TableCell>{row.error}</TableCell>
      </TableRow>,
    );
  });

  return (
    <Table>
      <>
        {rowElement?.length <= 0 ? (
          <Box
            sx={{
              width: "1300px",
              margin: 5,
              background: "#5cb85c",
              padding: 10,
            }}
          >
            <Typography align="center" variant="h5" sx={{ color: "white" }}>
              {" "}
              All {totalData} data have successfully been imported
            </Typography>
          </Box>
        ) : (
          <Table
            sx={{
              maxWidth: "1300px !important",
              display: "block",
              overflow: "auto",
            }}
          >
            <TableHead>
              <TableRow>
                <TableCell>Price Group</TableCell>
                <TableCell>Company ID</TableCell>
                <TableCell>Product Ref</TableCell>
                <TableCell>Error</TableCell>
                <TableCell>Error Details</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rowsPerPage > 0
                ? rowElement.slice(
                    page * rowsPerPage,
                    page * rowsPerPage + rowsPerPage,
                  )
                : rowElement}
            </TableBody>
            <TableFooter>
              <TableRow>
                <TablePagination
                  rowsPerPageOptions={[5, 10, 25, 200]}
                  colSpan={4}
                  count={rowElement.length}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  sx={{
                    overflow: "hidden",
                  }}
                  onPageChange={handleChangePage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  ActionsComponent={TablePaginationActions}
                />
              </TableRow>
            </TableFooter>
          </Table>
        )}
      </>
    </Table>
  );
};

const generateTableForCustomer = (
  result: any[],
  totalData: number,
  rowsPerPage: number,
  page: number,
  handleChangePage: (
    event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number,
  ) => void,
  handleChangeRowsPerPage: (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => void,
) => {
  const rowElement: JSX.Element[] = [];
  result.forEach((row: any) => {
    rowElement.push(
      <TableRow key={row.key.company_id}>
        <TableCell>{row.key.company_id}</TableCell>
        <TableCell>{row.error}</TableCell>
      </TableRow>,
    );
  });

  return (
    <Table>
      <>
        {rowElement?.length <= 0 ? (
          <Box
            sx={{
              width: "1300px",
              margin: 5,
              background: "#5cb85c",
              padding: 10,
            }}
          >
            <Typography align="center" variant="h5" sx={{ color: "white" }}>
              {" "}
              All {totalData} data have successfully been imported
            </Typography>
          </Box>
        ) : (
          <Table
            sx={{
              maxWidth: "1300px !important",
              display: "block",
              overflow: "auto",
            }}
          >
            <TableHead>
              <TableRow>
                <TableCell>Company ID</TableCell>
                <TableCell>Error Details</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rowsPerPage > 0
                ? rowElement.slice(
                    page * rowsPerPage,
                    page * rowsPerPage + rowsPerPage,
                  )
                : rowElement}
            </TableBody>
            <TableFooter>
              <TableRow>
                <TablePagination
                  rowsPerPageOptions={[5, 10, 25, 200]}
                  colSpan={4}
                  count={rowElement.length}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  sx={{
                    overflow: "hidden",
                  }}
                  onPageChange={handleChangePage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  ActionsComponent={TablePaginationActions}
                />
              </TableRow>
            </TableFooter>
          </Table>
        )}
      </>
    </Table>
  );
};

export const Result: React.FC<ResultProps> = ({
  totalData,
  result,
  featureType,
}) => {
  const [page, setPage] = React.useState(0);
  const [filteredRows, setFilteredRows] = React.useState<any>([]);
  const [rowsPerPage, setRowsPerPage] = React.useState(10);

  useEffect(() => {
    if (featureType === "CUSTOMER") {
      setFilteredRows([...(result.customer?.filter((p) => p.error) || [])]);
    } else {
      setFilteredRows([
        ...(result.product?.filter((p) => p.error) || []),
        ...(result.order_guide?.filter((p) => p.error) || []),
      ]);
    }
  }, [result]);

  const handleChangePage = (
    _event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number,
  ) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const getSuccessCount = () => {
    if (featureType === "CUSTOMER") {
      return result.customer?.filter((i) => !i.error).length || 0;
    }

    const productSuccess = result.product?.filter((i) => !i.error).length || 0;
    const orderGuideSuccess =
      result.order_guide?.filter((i) => !i.error).length || 0;

    return productSuccess + orderGuideSuccess;
  };

  const getFailedCount = () => {
    if (featureType === "CUSTOMER") {
      return result.customer?.filter((i) => !!i.error).length || 0;
    }

    const productFailed = result.product?.filter((i) => !!i.error).length || 0;
    const orderGuideFailed =
      result.order_guide?.filter((i) => !!i.error).length || 0;

    return productFailed + orderGuideFailed;
  };

  return (
    <Stack spacing={2}>
      {featureType === "CUSTOMER"
        ? generateTableForCustomer(
            filteredRows,
            totalData,
            rowsPerPage,
            page,
            handleChangePage,
            handleChangeRowsPerPage,
          )
        : generateTableForProduct(
            filteredRows,
            totalData,
            rowsPerPage,
            page,
            handleChangePage,
            handleChangeRowsPerPage,
          )}
    </Stack>
  );
};
